import React, { useState } from 'react';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';

import { apiService } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { LoginCredentials } from '../../types';

const loginSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required'),
}).required();

const signupSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
});

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();
  const { setAuth, refreshCurrentUser } = useAuthStore();

  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
    setValue: setLoginValue,
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const {
    register: registerSignup,
    handleSubmit: handleSignupSubmit,
    formState: { errors: signupErrors },
    reset: resetSignup,
  } = useForm({
    resolver: yupResolver(signupSchema),
  });

  const onLoginSubmit = async (data: any) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.login(data.email, data.password);
      const { user, access_token } = response.data;
      
      setAuth(user, access_token, access_token);
      
      // Refresh user data to get updated loginStreak
      await refreshCurrentUser();
      
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Login failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const onSignupSubmit = async (data: any) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.signup({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
      });

      // Auto-login after successful signup
      const { user, access_token } = response.data;
      if (user && access_token) {
        setAuth(user, access_token, access_token);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        // If auto-login fails, switch to login tab
        setTabValue(0);
        resetSignup();
        // Pre-fill email for convenience
        setLoginValue('email', data.email);
        setError('Account created successfully! Please sign in.');
        toast.success('Account created! Please sign in.');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create account';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(ellipse at center, #1a1a1a 0%, #000000 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, rgba(255,255,255,0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255,255,255,0.03) 0%, transparent 50%)
          `,
          animation: 'drift 20s ease-in-out infinite alternate',
        },
        '@keyframes drift': {
          '0%': { transform: 'translate(0, 0) rotate(0deg)' },
          '100%': { transform: 'translate(20px, -20px) rotate(1deg)' },
        },
      }}
    >
      <Card
        sx={{
          width: { xs: '90%', sm: 420 },
          maxWidth: 420,
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          p: { xs: 3, sm: 6 },
          position: 'relative',
          zIndex: 1,
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
          mx: { xs: 2, sm: 0 },
        }}
      >
        <Box textAlign="center" mb={4}>
          <Box
            component="img"
            src="/logo.png"
            alt="Group Legion Panel Logo"
            sx={{
              width: 'auto',
              height: 100,
              maxWidth: 200,
              mb: 2,
              mx: 'auto',
              display: 'block',
              objectFit: 'contain',
            }}
          />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #ffffff 0%, #888888 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1,
            }}
          >
            Group Legion Panel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Project Management & Time Tracking
          </Typography>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{
            mb: 3,
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 1,
            p: 0.5,
            '& .MuiTab-root': {
              flex: 1,
              color: 'rgba(255, 255, 255, 0.6)',
              borderRadius: 0.75,
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab label="Sign In" />
          <Tab label="Sign Up" />
        </Tabs>


        {error && (
          <Alert severity={error.includes('successfully') ? 'success' : 'error'} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Login Form */}
        {tabValue === 0 && (
          <Box component="form" onSubmit={handleLoginSubmit(onLoginSubmit)}>
            <TextField
              {...registerLogin('email')}
              fullWidth
              label="Email Address"
              type="email"
              error={!!loginErrors.email}
              helperText={loginErrors.email?.message}
              margin="normal"
              autoComplete="email"
              autoFocus
            />

            <TextField
              {...registerLogin('password')}
              fullWidth
              label="Password"
              type="password"
              error={!!loginErrors.password}
              helperText={loginErrors.password?.message}
              margin="normal"
              autoComplete="current-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Box>
        )}

        {/* Signup Form */}
        {tabValue === 1 && (
          <Box component="form" onSubmit={handleSignupSubmit(onSignupSubmit)}>
            <TextField
              {...registerSignup('firstName')}
              fullWidth
              label="First Name"
              error={!!signupErrors.firstName}
              helperText={signupErrors.firstName?.message}
              margin="normal"
              autoFocus
            />

            <TextField
              {...registerSignup('lastName')}
              fullWidth
              label="Last Name"
              error={!!signupErrors.lastName}
              helperText={signupErrors.lastName?.message}
              margin="normal"
            />

            <TextField
              {...registerSignup('email')}
              fullWidth
              label="Email Address"
              type="email"
              error={!!signupErrors.email}
              helperText={signupErrors.email?.message}
              margin="normal"
              autoComplete="email"
            />

            <TextField
              {...registerSignup('password')}
              fullWidth
              label="Password"
              type="password"
              error={!!signupErrors.password}
              helperText={signupErrors.password?.message}
              margin="normal"
              autoComplete="new-password"
            />

            <TextField
              {...registerSignup('confirmPassword')}
              fullWidth
              label="Confirm Password"
              type="password"
              error={!!signupErrors.confirmPassword}
              helperText={signupErrors.confirmPassword?.message}
              margin="normal"
              autoComplete="new-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 3 }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </Box>
        )}

      </Card>
    </Box>
  );
};

export default LoginPage;